import React from 'react'
import { Button, Card, CardContent, CardHeader, Input, Label, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { syncRewardsUsdtBrlRate } from '../../services/rewardsFxRateService'
import { getRewardsAppSettings, setRewardsAppSettings, type RewardsAppSettings } from '../../services/rewardsSettingsService'

function clampNum(v: number, min: number, max: number) {
  if (!Number.isFinite(v)) return min
  return Math.max(min, Math.min(max, v))
}

export default function RewardsSettingsFlagsCard() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [rateSyncing, setRateSyncing] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [cfg, setCfg] = React.useState<RewardsAppSettings | null>(null)

  const load = React.useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      setCfg(await getRewardsAppSettings())
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar configuracoes do Rewards')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  async function save() {
    if (!cfg) return
    setSaving(true)
    setError(null)
    try {
      await setRewardsAppSettings({
        enabled: cfg.enabled,
        syncEnabled: cfg.syncEnabled,
        trialEnabled: cfg.trialEnabled,
        homeEnabled: cfg.homeEnabled,
        trialMinVolumeUsdt: clampNum(cfg.trialMinVolumeUsdt, 0, 10_000_000),
        trialWindowDays: clampNum(cfg.trialWindowDays, 1, 30),
        trialPeriodDays: clampNum(cfg.trialPeriodDays, 1, 90),
        multiplierX: clampNum(cfg.multiplierX, 1, 20),
      })
      toast({ title: 'Rewards', description: 'Configuracoes salvas.', variant: 'success', soft: true })
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar')
      toast({ title: 'Rewards', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function syncRateNow() {
    setRateSyncing(true)
    try {
      const resp = await syncRewardsUsdtBrlRate()
      toast({
        title: 'Rewards',
        description: `Taxa USDT->BRL atualizada: ${resp.rate} (${resp.source}).`,
        variant: 'success',
        soft: true,
      })
      await load()
    } catch (e: any) {
      toast({ title: 'Rewards', description: e?.message || 'Falha ao atualizar taxa USDT->BRL', variant: 'error' })
    } finally {
      setRateSyncing(false)
    }
  }

  const blocked = saving || loading

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Rewards - Configuracoes globais (SSOT)</CardHeader>
      <CardContent className="space-y-4">
        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        {!cfg ? (
          <div className="text-sm text-foreground/70">{loading ? 'Carregando...' : 'Sem dados.'}</div>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-md border border-border/60 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">Modulo Rewards</div>
                    <div className="text-xs text-foreground/60">Feature flag global (UI/rotas).</div>
                  </div>
                  <Switch checked={cfg.enabled} disabled={blocked} onChange={(e) => setCfg({ ...cfg, enabled: (e.target as HTMLInputElement).checked })} />
                </div>
              </div>

              <div className="rounded-md border border-border/60 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">Sync Rewards</div>
                    <div className="text-xs text-foreground/60">Habilita rotinas de sincronizacao (cron).</div>
                  </div>
                  <Switch checked={cfg.syncEnabled} disabled={blocked} onChange={(e) => setCfg({ ...cfg, syncEnabled: (e.target as HTMLInputElement).checked })} />
                </div>
              </div>

              <div className="rounded-md border border-border/60 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">Trial Rewards (30d)</div>
                    <div className="text-xs text-foreground/60">Habilita start + guard do trial Rewards.</div>
                  </div>
                  <Switch checked={cfg.trialEnabled} disabled={blocked} onChange={(e) => setCfg({ ...cfg, trialEnabled: (e.target as HTMLInputElement).checked })} />
                </div>
              </div>

              <div className="rounded-md border border-border/60 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">Home: mostrar secao Rewards</div>
                    <div className="text-xs text-foreground/60">Exibe CTA/explicacao do Rewards na Home publica.</div>
                  </div>
                  <Switch checked={cfg.homeEnabled} disabled={blocked} onChange={(e) => setCfg({ ...cfg, homeEnabled: (e.target as HTMLInputElement).checked })} />
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Multiplicador economico (x)</Label>
                <Input
                  type="number"
                  value={cfg.multiplierX}
                  onChange={(e) => setCfg({ ...cfg, multiplierX: Number((e.target as HTMLInputElement).value) })}
                  disabled={blocked}
                />
                <div className="text-xs text-foreground/60">Ex.: 3 = para cada 1 de desconto, precisa 3 de comissao confirmada.</div>
              </div>

              <div className="space-y-1.5">
                <Label>Taxa USDT -&gt; BRL (automatica)</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={cfg.usdtBrlRate} disabled />
                  <Button variant="outline" onClick={syncRateNow} disabled={blocked || rateSyncing}>
                    {rateSyncing ? 'Atualizando...' : 'Atualizar agora'}
                  </Button>
                </div>
                <div className="text-xs text-foreground/60">Atualiza automaticamente via cron (fonte: AwesomeAPI).</div>
              </div>

              <div className="space-y-1.5">
                <Label>Trial: volume minimo (USDT)</Label>
                <Input
                  type="number"
                  value={cfg.trialMinVolumeUsdt}
                  onChange={(e) => setCfg({ ...cfg, trialMinVolumeUsdt: Number((e.target as HTMLInputElement).value) })}
                  disabled={blocked}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Trial: janela critica (dias)</Label>
                  <Input
                    type="number"
                    value={cfg.trialWindowDays}
                    onChange={(e) => setCfg({ ...cfg, trialWindowDays: Number((e.target as HTMLInputElement).value) })}
                    disabled={blocked}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Trial: duracao total (dias)</Label>
                  <Input
                    type="number"
                    value={cfg.trialPeriodDays}
                    onChange={(e) => setCfg({ ...cfg, trialPeriodDays: Number((e.target as HTMLInputElement).value) })}
                    disabled={blocked}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={blocked}>
                {saving ? 'Salvando...' : 'Salvar configuracoes'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

